import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Monitor, Building2, Briefcase, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { hasColumn } from '../lib/schemaProbe';
import { clearCountryCache } from '../lib/fiscal';
import BrandLogo from './BrandLogo';
import {
  ACTIVITY_TYPES, COUNTRIES, SECTORS, categoriesFor, labelOf, provincesFor,
  provinceFromPostalCode, isValidSpanishTaxId, normalizeTaxId, buildProfilePatch,
} from '../lib/companyOnboarding';

// ============================================================================
// First-login company onboarding (mounted by Layout).
//
// A brand-new OWNER — no tax id on the profile yet and no invoices — is asked
// for their activity type and billing details before using the app. Answers
// are written to the company profile (Configuración › Datos de empresa), so
// they print on the very first invoice. Team members, the super admin and
// gestorías never see it; neither does any account that already invoices.
//
// "Completar más tarde" hides it for the browser session; it comes back on the
// next login until the tax id is filled in (here or in Datos de empresa).
// ============================================================================

// Signup leaves role = 'user'; business_owner is the explicit owner role.
const OWNER_ROLES = [null, undefined, '', 'user', 'business_owner'];
const laterKey = (uid) => `zb_onboarding_later_${uid}`;

// WelcomeModal waits for this so the two first-login screens never stack:
// resolves once the onboarding has decided not to show, or has been finished.
let settledFor = null;
let settledDetail = null;
const waiters = new Set();
function settle(uid, detail = {}) {
  settledFor = uid;
  settledDetail = detail;
  waiters.forEach((w) => { if (w.uid === uid) { w.resolve(detail); waiters.delete(w); } });
}
export function onboardingSettled(uid) {
  if (settledFor === uid) return Promise.resolve(settledDetail);
  return new Promise((resolve) => waiters.add({ uid, resolve }));
}

const EMPTY = {
  activity: '', fiscalName: '', taxId: '', phone: '', street: '', city: '',
  postalCode: '', province: '', country: 'ES', sectorId: '', categoryId: '',
};

const ACTIVITY_ICONS = { autonomo: Monitor, empresa: Building2, asesoria: Briefcase };

const inputCls = 'w-full h-11 border border-[#d6dce2] rounded-md px-3 text-[15px] text-[#1d2d3d] bg-white outline-none focus:border-[#1a4f8b] focus:ring-2 focus:ring-[#1a4f8b]/20 disabled:bg-[#f4f7f9] disabled:text-[#9aa7b2]';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm text-[#1d2d3d] mb-1.5">
        {label}{required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

export default function CompanyOnboarding({ userId }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'es').slice(0, 2);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [startCountry, setStartCountry] = useState('ES');
  const [error, setError] = useState('');
  const [taxWarnedFor, setTaxWarnedFor] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        try { if (sessionStorage.getItem(laterKey(userId))) { settle(userId); return; } } catch { /* storage blocked */ }
        const { data: p, error: pErr } = await supabase
          .from('profiles')
          .select('role, employer_id, company_name, cif_nif, phone, address, country_code')
          .eq('id', userId)
          .single();
        if (cancelled) return;
        if (pErr || !p || p.employer_id || !OWNER_ROLES.includes(p.role) || String(p.cif_nif || '').trim()) {
          settle(userId);
          return;
        }
        // An account that already invoices isn't new — never interrupt it.
        const { count, error: cErr } = await supabase
          .from('invoices')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId);
        if (cancelled) return;
        if (cErr || (count && count > 0)) { settle(userId); return; }

        const country = p.country_code === 'PK' ? 'PK' : 'ES';
        setStartCountry(country);
        setForm({ ...EMPTY, fiscalName: p.company_name || '', phone: p.phone || '', street: p.address || '', country });
        setOpen(true);
      } catch {
        settle(userId); // never block the app on the onboarding
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // The app keeps rendering underneath: don't let it scroll behind the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const set = (k, v) => { setError(''); setForm((f) => ({ ...f, [k]: v })); };

  const pickActivity = (activity) => {
    setForm((f) => ({
      ...f,
      activity,
      // An asesoría's sector is obvious — preselect it (still editable).
      ...(activity === 'asesoria' && !f.sectorId ? { sectorId: 'accounting', categoryId: '' } : {}),
    }));
    setStep(2);
  };

  // Spanish postcode → province, unless the user picked a different one by hand.
  const onPostalCode = (cp) => {
    setError('');
    setForm((f) => {
      if (f.country !== 'ES') return { ...f, postalCode: cp };
      const auto = provinceFromPostalCode(cp);
      const wasAuto = !f.province || f.province === provinceFromPostalCode(f.postalCode);
      return { ...f, postalCode: cp, province: auto && wasAuto ? auto : f.province };
    });
  };

  const onCountry = (country) => {
    setError('');
    setForm((f) => ({ ...f, country, province: provincesFor(country).includes(f.province) ? f.province : '' }));
  };

  const later = () => {
    try { sessionStorage.setItem(laterKey(userId), '1'); } catch { /* noop */ }
    setOpen(false);
    settle(userId, { skipped: true });
  };

  const taxLabel = form.country === 'PK' ? t('onboard.taxIdPk') : t('onboard.taxId');

  const submit = async (e) => {
    e.preventDefault();
    const missing = [
      !form.fiscalName.trim() && t('onboard.fiscalName'),
      !form.taxId.trim() && taxLabel,
      !form.province && t('onboard.province'),
    ].filter(Boolean);
    if (missing.length) { setError(t('onboard.required', { fields: missing.join(', ') })); return; }

    // A wrong NIF prints on every invoice: flag it, but a second press saves
    // anyway (the check can't know every special-case id).
    if (form.country === 'ES' && !isValidSpanishTaxId(form.taxId)) {
      const id = normalizeTaxId(form.taxId);
      if (taxWarnedFor !== id) {
        setTaxWarnedFor(id);
        setError(t('onboard.taxIdInvalid', { id }));
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const [city, postal, province, activity] = await Promise.all([
        hasColumn('profiles', 'city'), hasColumn('profiles', 'postal_code'),
        hasColumn('profiles', 'province'), hasColumn('profiles', 'activity_type'),
      ]);
      const patch = buildProfilePatch(form, { lang, structured: city && postal && province, activityColumn: activity });
      // RLS filters an unauthorised UPDATE silently — read the row back.
      const { data, error: upErr } = await supabase.from('profiles').update(patch).eq('id', userId).select('id');
      if (upErr) throw upErr;
      if (!data?.length) throw new Error('profile not updated');

      if (patch.country_code !== startCountry) {
        // The whole app (tax regime, currency, language) keys off the country:
        // start clean rather than patch every mounted screen.
        clearCountryCache();
        try { localStorage.setItem('zbilling_last_country', patch.country_code); } catch { /* noop */ }
        window.location.reload();
        return;
      }
      setOpen(false);
      settle(userId, { company_name: patch.company_name });
    } catch (err) {
      setError(t('onboard.saveError', { msg: err.message || String(err) }));
    } finally {
      setSaving(false);
    }
  };

  const categories = categoriesFor(form.sectorId);

  return (
    <div className="fixed inset-0 z-[80] bg-white overflow-y-auto overscroll-contain font-sans" role="dialog" aria-modal="true">
      <header className="border-b border-[#e6eaee]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2">
          <BrandLogo height={36} />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 relative">
        {step === 2 && (
          <button
            type="button"
            onClick={() => { setError(''); setStep(1); }}
            aria-label={t('onboard.back')}
            className="absolute left-4 sm:left-6 top-8 p-1.5 rounded-lg text-[#1a4f8b] hover:bg-[#eaf1fb]"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}

        {step === 1 ? (
          <div className="max-w-[360px] mx-auto text-center">
            <h1 className="text-2xl font-bold text-[#1d2d3d]">{t('onboard.welcomeTitle')}</h1>
            <p className="text-[#6d7d8b] mt-1">{t('onboard.welcomeSubtitle')}</p>
            <p className="text-[#1a4f8b] font-semibold mt-8 mb-4">{t('onboard.activityQuestion')}</p>
            <div className="space-y-2.5 text-left">
              {ACTIVITY_TYPES.map((a) => {
                const Icon = ACTIVITY_ICONS[a];
                const active = form.activity === a;
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => pickActivity(a)}
                    className={`w-full text-left flex items-center gap-4 px-6 py-5 rounded-md border transition-colors ${active ? 'border-[#1a4f8b] bg-[#f4f7fb]' : 'border-[#d6dce2] hover:border-[#1a4f8b] hover:bg-[#f8fafc]'}`}
                  >
                    <Icon className="w-6 h-6 text-[#1a4f8b] flex-shrink-0" strokeWidth={1.5} />
                    <span>
                      <span className="block text-[#1d2d3d]">{t(`onboard.activity_${a}`)}</span>
                      <span className="block text-xs text-[#6d7d8b] mt-0.5">{t(`onboard.activityDesc_${a}`)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={later} className="mt-8 text-sm text-[#6d7d8b] hover:text-[#1a4f8b] hover:underline">
              {t('onboard.later')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-[800px] mx-auto pt-10 sm:pt-0" noValidate>
            <h1 className="text-2xl font-bold text-[#1d2d3d]">{t('onboard.detailsTitle')}</h1>
            <p className="text-[#1d2d3d] mt-1">{t('onboard.detailsSubtitle')}</p>
            <div className="border-t border-[#e6eaee] mt-3 mb-6" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-14 gap-y-5">
              <Field label={t('onboard.fiscalName')} required>
                <input className={inputCls} value={form.fiscalName} autoFocus autoComplete="organization"
                  placeholder={form.activity === 'autonomo' ? t('onboard.fiscalNamePhAutonomo') : t('onboard.fiscalNamePhEmpresa')}
                  onChange={(e) => set('fiscalName', e.target.value)} />
              </Field>
              <Field label={taxLabel} required>
                <input className={`${inputCls} uppercase`} value={form.taxId} autoComplete="off"
                  onChange={(e) => { setTaxWarnedFor(null); set('taxId', e.target.value); }} />
              </Field>
              <Field label={t('onboard.phone')}>
                <input className={inputCls} type="tel" value={form.phone} autoComplete="tel"
                  placeholder={t('onboard.phone')} onChange={(e) => set('phone', e.target.value)} />
              </Field>
              <Field label={t('onboard.address')}>
                <input className={inputCls} value={form.street} autoComplete="street-address"
                  placeholder={t('onboard.addressPh')} onChange={(e) => set('street', e.target.value)} />
              </Field>
              <Field label={t('onboard.city')}>
                <input className={inputCls} value={form.city} autoComplete="address-level2"
                  onChange={(e) => set('city', e.target.value)} />
              </Field>
              <Field label={t('onboard.postalCode')}>
                <input className={inputCls} value={form.postalCode} autoComplete="postal-code" inputMode="numeric"
                  onChange={(e) => onPostalCode(e.target.value)} />
              </Field>
              <Field label={t('onboard.province')} required>
                <select className={inputCls} value={form.province} onChange={(e) => set('province', e.target.value)}>
                  <option value="">{t('onboard.provincePh')}</option>
                  {provincesFor(form.country).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label={t('onboard.country')}>
                <select className={inputCls} value={form.country} onChange={(e) => onCountry(e.target.value)}>
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{labelOf(c, lang)}</option>)}
                </select>
                {form.country !== startCountry && (
                  <span className="block text-xs text-[#b26a00] mt-1">{t('onboard.countryNote')}</span>
                )}
              </Field>
              <Field label={t('onboard.sector')}>
                <select className={inputCls} value={form.sectorId}
                  onChange={(e) => setForm((f) => ({ ...f, sectorId: e.target.value, categoryId: '' }))}>
                  <option value="">{t('onboard.sectorPh')}</option>
                  {SECTORS.map((s) => <option key={s.id} value={s.id}>{labelOf(s, lang)}</option>)}
                </select>
              </Field>
              <Field label={t('onboard.category')}>
                <select className={inputCls} value={form.categoryId} disabled={!categories.length}
                  onChange={(e) => set('categoryId', e.target.value)}>
                  <option value="">{t('onboard.categoryPh')}</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{labelOf(c, lang)}</option>)}
                </select>
              </Field>
            </div>

            {error && (
              <div className="mt-6 rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-700">{error}</div>
            )}

            <div className="border-t border-[#e6eaee] mt-10 pt-6 flex flex-col items-center gap-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full max-w-[344px] h-11 rounded-full bg-[#fca311] hover:bg-[#e0920b] text-white font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('onboard.saving')}</> : t('onboard.continue')}
              </button>
              <button type="button" onClick={later} disabled={saving} className="text-sm text-[#6d7d8b] hover:text-[#1a4f8b] hover:underline">
                {t('onboard.later')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
